import { readCache, writeCache } from '../lib/offlineCache'
import { int, isRecord, oneOf, str, text } from '../sync/read'
import type { RemoteConfig } from './types'

/** Used until `/config` has ever answered (first run, or a blocked cache): the address support mail used to be
 * baked in as, before `supportEmail` existed. */
export const DEFAULT_SUPPORT_EMAIL = 'macrotide@ikarosdev.com'

/** Below any real server `consentVersion` (which starts at 1), so a config that hasn't loaded yet never forces a
 * re-consent nobody asked for. */
const DEFAULT_CONSENT_VERSION = 0

export const DEFAULT_REMOTE_CONFIG: RemoteConfig = {
  features: {},
  minVersion: { android: null, web: null },
  maintenance: null,
  consentVersion: DEFAULT_CONSENT_VERSION,
  supportEmail: DEFAULT_SUPPORT_EMAIL,
  vapidPublicKey: '',
}

function readMaintenance(value: unknown): RemoteConfig['maintenance'] {
  if (!isRecord(value)) return null
  const message = text(value.message)
  if (message === null) return null
  return { message, level: oneOf(value.level, ['info', 'warning'], 'info') }
}

function readMinVersion(value: unknown): RemoteConfig['minVersion'] {
  const record = isRecord(value) ? value : {}
  return { android: int(record.android), web: str(record.web) }
}

function readFeatures(value: unknown): Record<string, boolean> {
  if (!isRecord(value)) return {}
  const features: Record<string, boolean> = {}
  for (const [key, flag] of Object.entries(value)) {
    if (typeof flag === 'boolean') features[key] = flag
  }
  return features
}

/** `GET /config`'s answer (docs/plan/api.md), tolerant of a missing or malformed field so an old cached copy or a
 * server still rolling out a new field never breaks the app. */
export function parseRemoteConfig(value: unknown): RemoteConfig {
  const record = isRecord(value) ? value : {}
  return {
    features: readFeatures(record.features),
    minVersion: readMinVersion(record.minVersion),
    maintenance: readMaintenance(record.maintenance),
    consentVersion: int(record.consentVersion) ?? DEFAULT_CONSENT_VERSION,
    supportEmail: text(record.supportEmail) ?? DEFAULT_SUPPORT_EMAIL,
    vapidPublicKey: str(record.vapidPublicKey) ?? '',
  }
}

const CACHE_KEY = 'macrotide.config.cache'

export function readCachedRemoteConfig(): RemoteConfig | null {
  const cached = readCache<unknown>(CACHE_KEY)
  return cached === null ? null : parseRemoteConfig(cached)
}

export function writeCachedRemoteConfig(config: RemoteConfig): void {
  writeCache(CACHE_KEY, config)
}

/** A feature switched off server-side or by an app-only `appFlags` entry (docs/plan/api.md "App config"); missing
 * entirely reads as on, so nothing changes until a manager/admin turns one off. */
export function featureEnabled(config: RemoteConfig, key: string): boolean {
  return config.features[key] !== false
}

/** `authApi.ts` runs before any React tree (sign-in), so it reads the support address straight from the cache
 * rather than through `useRemoteConfig`. */
export function cachedSupportEmail(): string {
  return readCachedRemoteConfig()?.supportEmail ?? DEFAULT_SUPPORT_EMAIL
}

/** Whether [build] (a `major.minor.patch`-ish version string) is older than [floor]; a missing or non-numeric part
 * reads as 0 rather than throwing, so "1.2" < "1.2.1" and a value that isn't a version at all just compares as if
 * it were "0". */
export function versionBelow(build: string, floor: string): boolean {
  const parts = (version: string) => version.split('.').map((part) => Number.parseInt(part, 10) || 0)
  const [a, b] = [parts(build), parts(floor)]
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const diff = (a[i] ?? 0) - (b[i] ?? 0)
    if (diff !== 0) return diff < 0
  }
  return false
}
